import {
    Component,
    ChangeDetectionStrategy,
    NgZone,
    Input,
    Output,
    EventEmitter,
    SimpleChanges,
    OnDestroy,
} from '@angular/core';
import { Evse } from '@kempoweroyj/ngx-evse-api-client';
import { Transaction } from '@kempoweroyj/ngx-transactions-api-client';
import { chart, Options, XAxisPlotBandsOptions, XrangePointOptionsObject } from 'highcharts';
import {
    isAvailable,
    isCharging,
    isFaulted,
    isOccupied,
    isSelfTest,
} from 'shared/connector-status-pipes/connector-status.pipe';
import { isEmpty as _isEmpty, escape as escapeHtml, round, cloneDeep as _cloneDeep, last } from 'lodash';
import * as moment from 'moment';

import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts);
import HighchartsXrange from 'highcharts/modules/xrange';
HighchartsXrange(Highcharts);
import HighchartsCustomEvents from 'highcharts-custom-events';
import { GetEvseStatusHistoryResponse, StatusHistoryRow } from 'api/status.api';
import { GetConnectionsResponse } from 'api/connections.api';
import { DurationPipe } from 'shared/pipes/date/duration.pipe';
import {
    calculateDurationAndZoomLevel,
    TimelineZoomLevel,
} from 'locations/station-dialog/status/timeline-controls/timeline-controls.component';
import { TimeDisplay, TimeDisplayService } from 'shared/time-display-service/time-display.service';
import { AdjustUtcDatePipe } from 'shared/pipes/date/adjust-utc-date/adjust-utc-date.pipe';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';

HighchartsCustomEvents(Highcharts);

let GraphId = 0;

const baseOptions: () => Options = () => ({
    time: {
        timezoneOffset: new Date().getTimezoneOffset(),
    },
    yAxis: {
        title: { text: null },
        categories: [],
        reversed: true,
        tickmarkPlacement: 'on',
        gridLineColor: '#898e90',
        offset: -16,
        showEmpty: true,
        labels: {
            style: {
                width: 70,
            },
        },
    },
    xAxis: {
        title: { text: null },
        type: 'datetime',
        tickWidth: 0,
        lineWidth: 0,
        lineColor: 'transparent',
        labels: {
            enabled: false,
        },
        crosshair: {
            width: 1,
            snap: false,
        },
    },
    legend: {
        enabled: false,
    },
    plotOptions: {
        xrange: {
            grouping: false,
        },
        series: {
            animation: false,
        },
    },
    credits: { enabled: false },
    title: null,
});

type SessionPointType = 'beforeCharging' | 'charging' | 'afterCharging';

export interface SessionPoint extends XrangePointOptionsObject {
    custom: Transaction;
    pointType?: SessionPointType;
}

export interface StatusPoint extends XrangePointOptionsObject {
    custom: StatusHistoryRow;
}

@Component({
    selector: 'ce-timeline-chart',
    templateUrl: './timeline-chart.component.html',
    styleUrls: ['./timeline-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TimelineChartComponent implements OnDestroy {
    @Input() connectors: Evse[];
    @Input() txs: Transaction[] = [];
    @Input() connectorStatusHistory: GetEvseStatusHistoryResponse[];
    @Input() connections: GetConnectionsResponse;
    @Input() startTime: string;
    @Output() startTimeChange = new EventEmitter<string>();
    @Input() endTime: string;
    @Output() endTimeChange = new EventEmitter<string>();
    @Input() seekTime: string;
    @Output() seekTimeChange = new EventEmitter<string>();
    @Input() zoomLevel: TimelineZoomLevel;
    @Output() zoomLevelChange = new EventEmitter<TimelineZoomLevel>();
    @Output() txClicked = new EventEmitter<Transaction>();
    @Output() connectorLabelClicked = new EventEmitter<Evse>();

    /** Timestamps when charger has rebooted */
    @Input() bootTimes: string[] = [];

    graphId = `timeline-chart-${++GraphId}`;
    desiredHeightPx = 400;

    chart: Highcharts.Chart;

    currentTime: TimeDisplay;

    onDestroy$: Subject<void> = new Subject();

    constructor(
        private ngZone: NgZone,
        private durationPipe: DurationPipe,
        private timeDisplayService: TimeDisplayService,
        private adjustUtcDatePipe: AdjustUtcDatePipe
    ) {
        this.timeDisplayService.timeObservable$
            .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
            .subscribe((time) => {
                this.currentTime = time;
                if (this.chart) {
                    this.update();
                    this.updatePlotlines();
                }
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            this.txs &&
            this.connectors &&
            (changes.connectors ||
                changes.txs ||
                changes.startTime ||
                changes.endTime ||
                changes.connectorStatusHistory ||
                changes.connections ||
                changes.bootTimes)
        ) {
            const STATIC_HEIGHT_PX = 70;
            const LANE_HEIGHT_PX = 24;
            this.desiredHeightPx = STATIC_HEIGHT_PX + this.connectors.length * LANE_HEIGHT_PX;
            setTimeout(() => {
                if (!this.chart) {
                    this.init();
                    this.updatePlotlines();
                } else {
                    this.update();
                    this.updatePlotlines();
                }
            });
        }
        if (changes.seekTime && this.chart) {
            this.updatePlotlines();
        }
    }
    updatePlotlines() {
        this.ngZone.runOutsideAngular(() => {
            this.chart.update({
                xAxis: {
                    plotLines: [
                        {
                            value: Date.parse(this.seekTime),
                            width: 1,
                            color: '#FF0000',
                            zIndex: 13,
                        },
                        ...(this.bootTimes || []).map((ts) => ({
                            value: Date.parse(ts),
                            width: 1,
                            color: '#000',
                            zIndex: 25,
                            className: 'stationreboot',
                            label: {
                                text: constructTitleForReboot(),
                                useHTML: true,
                                rotation: 0,
                            },
                        })),
                    ],
                },
            });
        });
    }

    init() {
        const options = this.getOptions();
        this.ngZone.runOutsideAngular(() => {
            this.chart = chart(this.graphId, options);
            setTimeout(() => {
                (this.chart.pointer as any).chartPosition = null;
            }, 3000);
        });
    }

    update() {
        const options = this.getOptions();
        this.ngZone.runOutsideAngular(() => {
            this.chart.update(options);
        });
    }

    getOptions() {
        // todo: fix
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const formatDateFunction = this.formatDateToUtc.bind(this);
        const _options = baseOptions();

        _options.time.timezoneOffset = this.currentTime.utcTimezoneOffset ?? new Date().getTimezoneOffset();

        const sessionPoints = this.mapSessions();

        const options: Options = {
            ..._options,
            chart: {
                height: this.desiredHeightPx,
                marginTop: 27,
                spacingLeft: 0,
                zooming: {
                    type: 'x',
                },
                events: {
                    click: function (ev) {
                        const x = new Date(this.xAxis[0].toValue(ev.chartX)).toISOString();
                        self.ngZone.run(() => {
                            self.seekTimeChange.next(x);
                        });
                    },
                    selection: function (ev) {
                        if (ev.xAxis) {
                            self.ngZone.run(() => {
                                ev.originalEvent.preventDefault(); // prevent default zooming action, send new values to redraw chart, hide reset-zoom button

                                const startTime = moment(ev.xAxis[0].min).toISOString();
                                const endTime = moment(ev.xAxis[0].max).toISOString();

                                const { zoomLevel } = calculateDurationAndZoomLevel(startTime, endTime);

                                self.startTimeChange.emit(startTime);
                                self.endTimeChange.emit(endTime);
                                self.seekTimeChange.emit(startTime);
                                self.zoomLevelChange.emit(zoomLevel);
                            });
                        }
                        return false;
                    },
                },
            },
            tooltip: {
                shared: true,
                shadow: false,
                borderWidth: 0,
                backgroundColor: 'transparent',
                padding: 0,
                positioner: function (w, h) {
                    return {
                        x: this.chart.plotLeft,
                        y: this.chart.chartHeight - h,
                    };
                },
                formatter: function () {
                    const custom = (this.points[0].point as any).custom;

                    if (custom?.schema === 'statusHistory') {
                        const status: StatusHistoryRow = custom;
                        const date = formatDateFunction(status.statusDate);
                        let str = `${date} - <b>${status.evseId}</b> - ${status.status}`;

                        if (status.reason) {
                            str += `<br>Reason: ${status.reason.split(',').join(', ')}`;
                        }

                        if (status.detail) {
                            str += `<br>Detail: ${status.detail.split(',').join(', ')}`;
                        }
                        return str;
                    } else {
                        const tx: Transaction = custom;
                        const pointType = (this.points[0].point as any).pointType as SessionPointType;
                        const evStr = tx.evIdentify?.displayName ? `${tx.evIdentify?.displayName} ` : '';
                        const socStr = tx.soc ? `<b> ${Math.round(tx.soc)} </b> %` : '';
                        const totalChargedStr = tx.chargedEnergyKwh
                            ? `+<b>${round(tx.chargedEnergyKwh, 1)}</b> kWh`
                            : '';
                        /** AC transactions doesn't have SoC */
                        const hasSoc = !!tx.soc;

                        if (pointType === 'beforeCharging') {
                            const date = formatDateFunction(tx.startTime);
                            const durationStr = tx.chargingStartTime
                                ? `- duration <b>${self.durationPipe.transform(Date.parse(tx.chargingStartTime) - Date.parse(tx.startTime))}</b>`
                                : '';

                            return `Cable plugged in: ${date}${durationStr} - ${evStr}`;
                        } else if (pointType === 'charging') {
                            const date = formatDateFunction(tx.chargingStartTime);

                            const endTime = tx.chargingEndTime ? Date.parse(tx.chargingEndTime) : Date.now();
                            const durationStr =
                                endTime && tx.chargingStartTime
                                    ? `- duration <b>${self.durationPipe.transform(endTime - Date.parse(tx.chargingStartTime))}</b>`
                                    : '';

                            // Audi e-Tron 87.5 kWh
                            // 52% > 85%, +<b>{{connector.tx.chargedEnergyKwh|number:'1.1-1'}}</b> kWh
                            if (tx.chargedEnergyKwh > 0) {
                                return `Charging: ${date}${durationStr} - <b>${tx.maxPowerKw} </b> kW, ${hasSoc ? `<b>${tx.socStart}</b> % ▸ ${socStr}, ` : ''}${totalChargedStr}<br>${evStr} `;
                            } else {
                                return `${date} - ${hasSoc ? socStr + ' -' : ''} ${evStr}`;
                            }
                        } else if (pointType === 'afterCharging') {
                            const date = formatDateFunction(tx.chargingEndTime);
                            const durationStr = tx.endTime
                                ? `- duration <b>${self.durationPipe.transform(Date.parse(tx.endTime) - Date.parse(tx.chargingEndTime))}</b>`
                                : '';

                            return `Idle: ${date}${durationStr} - ${evStr}`;
                        } else {
                            return '';
                        }
                    }
                },
            },
            xAxis: {
                ..._options.xAxis,
                min: this.startTime ? Date.parse(this.startTime) : undefined,
                max: this.endTime ? Date.parse(this.endTime) : undefined,
                plotBands: [..._cloneDeep(this.mapOfflineStatus())],
            },
            yAxis: {
                ..._options.yAxis,
                max: this.connectors.length - 1,
                categories: this.connectors.map((_, i) => `${i}`) as any,
                labels: {
                    style: {
                        padding: '8px',
                    },
                    useHTML: true,
                    events: {
                        click: function () {
                            // @ts-expect-error: `this' refers to highcharts custom events plugin's context
                            const connector: Evse = self.connectors[this.pos];
                            self.ngZone.run(() => {
                                self.connectorLabelClicked.emit(connector);
                            });
                        } as any,
                    },
                    formatter: function () {
                        const connector: Evse = self.connectors[+this.value];
                        let className = 'unavailable';
                        if (isCharging(connector)) {
                            className = 'charging';
                        } else if (isOccupied(connector)) {
                            className = 'occupied';
                        } else if (isFaulted(connector)) {
                            className = 'faulted';
                        } else if (isAvailable(connector)) {
                            className = 'available';
                        } else if (isSelfTest(connector)) {
                            className = 'self-test';
                        }
                        const name = connector?.evseName || connector?.evseId;
                        return `<span class="connector-label ${className}">` + escapeHtml(name) + '</span>';
                    },
                } as Highcharts.YAxisLabelsOptions,
            },
            series: [
                {
                    type: 'xrange',
                    name: 'Charging session',
                    data: sessionPoints.charging,
                    minPointLength: 1,
                    pointWidth: 12,
                    borderRadius: 0,
                    pointPadding: 0,
                    borderWidth: 0,
                    zIndex: 12,
                    states: {
                        inactive: {
                            opacity: 1,
                        },
                    },
                    point: {
                        events: {
                            click: function (ev: any) {
                                const tx: Transaction = ev.point.custom;
                                self.ngZone.run(() => {
                                    self.txClicked.emit(tx);
                                });
                            },
                        },
                    },
                },
                {
                    type: 'xrange',
                    name: 'Idle sessions',
                    data: sessionPoints.idle,
                    minPointLength: 1,
                    pointWidth: 6,
                    borderRadius: 0,
                    pointPadding: 0,
                    borderWidth: 0,
                    zIndex: 11,
                    animation: false,
                    states: {
                        inactive: {
                            opacity: 1,
                        },
                    },
                    point: {
                        events: {
                            click: function (ev: any) {
                                const tx: Transaction = ev.point.custom;
                                self.ngZone.run(() => {
                                    self.txClicked.emit(tx);
                                });
                            },
                        },
                    },
                },
                {
                    type: 'xrange',
                    name: 'Connector status',
                    data: this.mapConnectorStatus(),
                    minPointLength: 1,
                    pointWidth: 6,
                    borderRadius: 0,
                    pointPadding: 0,
                    borderWidth: 0,
                    opacity: 1,
                    zIndex: 10,
                    animation: false,
                    states: {
                        inactive: {
                            opacity: 1,
                        },
                        hover: {
                            enabled: false,
                        },
                    },
                    point: {
                        events: {
                            click: function (e: any) {
                                e.point.custom;
                                self.ngZone.run(() => {
                                    // self.txClicked.emit(tx);
                                });
                            },
                        },
                    },
                },
            ],
        };

        return options;
    }

    mapSessions() {
        const res: {
            idle: SessionPoint[];
            charging: SessionPoint[];
        } = {
            charging: [],
            idle: [],
        };

        /** Same as occupiedColor */
        const idleColor = '#808080';
        const warningColor = '#f3a73b';
        const chargingColor = '#78a9cc';

        this.txs.forEach((tx) => {
            const isErrored = tx.chargingEndReason === 'Timeout' || tx.chargingEndReason === 'DeAuthorized';

            const point: SessionPoint = {
                custom: tx,
                y: this.connectors.findIndex((c) => c.evseId === tx.evseId) || 0,
            };

            // Idle time before charging = (startTime - (chargingStartTime || tx.updateTime))
            if (tx.startTime) {
                res.idle.push({
                    ...point,
                    x: Date.parse(tx.startTime),
                    x2: Date.parse(tx.chargingStartTime || tx.updateTime),
                    color: isErrored ? warningColor : idleColor,
                    pointType: 'beforeCharging',
                });
            }

            // Charging time = (chargingStartTime - (chargingEndTime || tx.updateTime))
            if (tx.chargingStartTime) {
                res.charging.push({
                    ...point,
                    x: Date.parse(tx.chargingStartTime),
                    x2: Date.parse(tx.chargingEndTime || tx.updateTime),
                    color: isErrored ? warningColor : chargingColor,
                    pointType: 'charging',
                });
            }

            // Idle time after charging = (chargingEndTime - endTime || tx.updateTime)
            if (tx.chargingEndTime) {
                res.idle.push({
                    ...point,
                    x: Date.parse(tx.chargingEndTime),
                    x2: Date.parse(tx.endTime || tx.updateTime),
                    color: isErrored ? warningColor : idleColor,
                    pointType: 'afterCharging',
                });
            }
        });

        return res;
    }

    mapConnectorStatus(): StatusPoint[] {
        const points: StatusPoint[] = [];

        this.connectors.forEach((_connector, _index) => {
            const connector = this.connectorStatusHistory?.find((e) => e.rows[0]?.evseId === _connector.evseId);

            connector?.rows.forEach((row, index) => {
                const colors = {
                    available: '#DDEBDE',
                    charging: '#78a9cc',
                    faulted: '#eb3223',
                    occupied: '#E0E0E0',
                    offline: '#bebebe',
                    operative: '#DDEBDE',
                    reserved: 'orange', // TODO Reserved color
                    unavailable: '#bebebe',
                    unknown: '#bebebe',
                    selfTest: '#ffc054',
                };

                const statusDate = Date.parse(row.statusDate);
                const startTime = Date.parse(this.startTime);

                /** This line starts at statusDate or at the startTime if statusDate is outside of current view */
                const x = statusDate < startTime ? startTime : statusDate;

                /** This line ends where the next line starts. Uses current time instead if this is the last line, */
                const x2 =
                    index < connector.rows.length - 2
                        ? Date.parse(connector.rows[index + 1].statusDate)
                        : new Date().getTime();

                const y = this.connectors.findIndex((c) => c.evseId === _connector.evseId);

                let color = colors[row.status.toLowerCase()] || colors['unknown'];
                if (row.reason === 'POWER_ON_SELF_TEST') {
                    color = colors['selfTest'];
                }

                points.push({
                    x,
                    x2,
                    color: color,
                    custom: row,
                    y,
                } as StatusPoint);
            });
        });

        return points;
    }

    mapOfflineStatus(): XAxisPlotBandsOptions[] {
        const bands: XAxisPlotBandsOptions[] = [];
        const connections = this.connections?.pastConnections.slice() || [];
        connections?.sort((a, b) => {
            return Date.parse(a?.connectionStartTime) - Date.parse(b?.connectionStartTime);
        }); // ascending

        /** End time in this chart */
        const endTime = Date.parse(this.endTime);
        /** Start time in this chart */
        const startTime = Date.parse(this.startTime);

        /** Rear connection at start of the chart */
        const rearStartConnection = this.connections?.rearStartConnection;
        /** Rear connection at end of the chart */
        const rearEndConnection = this.connections?.rearEndConnection;

        if (connections.length > 0) {
            if (
                !_isEmpty(rearStartConnection) &&
                rearStartConnection.connectionStartTime < connections[0].connectionStartTime
            ) {
                connections.unshift(rearStartConnection);
            }

            // getConnections?consolidate=true returns current active connection in the past connections array.
            if (!_isEmpty(rearEndConnection)) {
                if (rearEndConnection.connectionStartTime > last(connections).connectionStartTime) {
                    connections.push(rearEndConnection); // rearEndConnection is most recent connection. Added to end of array.
                } else {
                    connections.splice(connections.length - 1, 1, rearEndConnection); // rearEndConnection happened before last connection in array. Replace.
                }
            }

            connections.forEach((con, index) => {
                // If first connection starts after startTime. Draw a band from startTime until first connection startTime */
                if (index === 0) {
                    const firstConnectionStartTime = Date.parse(con?.connectionStartTime);
                    if (firstConnectionStartTime > startTime) {
                        bands.push({
                            from: startTime,
                            to: firstConnectionStartTime,
                            // no label, as we do not know when the offline period actually starts - ends
                            className: 'station-offline',
                            zIndex: 20,
                        });
                    }
                }

                /** When this connection ended */
                const conEndTime = Date.parse(con.connectionEndTime);

                /** When next connection was made */
                let conStartTime: number;

                let isStillOffline = false;

                // The next connection should have connectionStartTime
                if (index < connections.length - 1 && connections[index + 1].connectionStartTime) {
                    conStartTime = Date.parse(connections[index + 1].connectionStartTime);
                } else {
                    isStillOffline = true;
                    conStartTime = new Date().getTime();
                }

                // Generate human-friendly dates
                const conEndDate = this.formatDateToUtc(moment(conEndTime).toISOString());
                const conStartDate = this.formatDateToUtc(moment(conStartTime).toISOString());

                // Draw band if connection has ended && (current connection end time [offline band starts] and next connection start time [offline bands end]) is relevant to the chart view
                if (conEndTime && conEndTime < endTime && conStartTime > startTime) {
                    const text = constructTitleForOfflineBand(
                        isStillOffline,
                        conEndDate,
                        conStartDate,
                        conStartTime,
                        conEndTime,
                        this.durationPipe
                    );

                    bands.push({
                        from: conEndTime < startTime ? startTime : conEndTime,
                        to: conStartTime > endTime ? endTime : conStartTime,
                        label: { text, useHTML: true },
                        className: 'station-offline',
                        zIndex: 20,
                    });
                }
            });
        } else {
            // No past connections in selected time range. Draw a band.
            const currentTime = new Date().getTime();
            bands.push({
                from: startTime,
                to: currentTime,
                // no label, as we do not know when the offline period starts - ends
                className: 'station-offline',
                zIndex: 20,
            });
        }
        return bands;
    }

    private formatDateToUtc(date: string) {
        return this.adjustUtcDatePipe.transform(date, {
            format: 'DD.MM.yyyy HH:mm',
            time: this.currentTime,
            showUtcTimeLabel: false,
        });
    }
}

function constructTitleForOfflineBand(
    isStillOffline: boolean,
    conEndDate: string,
    conStartDate: string,
    conStartTime: number,
    conEndTime: number,
    durationPipe: DurationPipe
) {
    let text = `<div class="reason"><div class="inner" title="`;

    if (!isStillOffline) {
        text += `${conEndDate} - ${conStartDate} \nWas offline for ${durationPipe.transform(conStartTime - conEndTime)}`;
    } else {
        text += `Offline since ${conEndDate} \nHas been offline for ${durationPipe.transform(conStartTime - conEndTime)}`;
    }

    text += `"></div></div>`;
    return text;
}

function constructTitleForReboot(): string {
    const title = `Charger booted up`;
    return `<div class="reboot reason"><div class="inner" title="${title}"></div></div>`;
}
